import { memo, useMemo } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { compact } from 'lodash-es';
import { InvoicePaymentDisplayStatusType } from 'anchor-scout-common/types';
import { TableListRow, Typography, TableListActions, DropdownMenuItem, ConfirmationModal, WithInfoIcon } from 'seagull';
import { Download, Void, MoneyReceive, LinkIcon, MailSentLine18, AgreementsListLine, RedoLiner24 } from 'seagull/icons';
import { allowResendAction, allowUnmarkPaidExternallyAction, allowUnvoidAction, allowVoidAction, getPaymentStatusText, getStatusLabel } from "../../business-logic/invoiceLogic";
import { InvoiceAccountingIntegrationStatus } from "../InvoiceAccountingIntegrationStatus";
import { routes } from "../../constants";
import { useOverlayContext, useStore, useStoreActions } from "../../contexts";
import { useDownloadInvoicePdf } from "../../hooks/invoice/useDownloadInvoicePdf";
import { useCopyDirectLink } from "../../hooks/useCopyDirectLink";
import { useFeatureFlags } from "../../hooks/useFeatureFlags";
import { usePaidExternally } from "../../hooks/usePaidExternally";
import { useResendInvoice } from "../../hooks/useResendInvoice";
import { Intent } from "../../infra/access-control";
import { invoiceModel } from "../../models";
import { selectors } from "../../store/state-selectors";
import { Accounting } from "../../types/api.model";
import styles from "./InvoicesTable.module.scss";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
const voidLoader = 'void';
const displayedStatusToSkin = {
  issued: undefined,
  overdue: 'error',
  paymentInitiated: 'success',
  paid: 'success',
  voided: undefined,
  refunded: 'standard',
  refundFailed: 'error',
  disputed: 'error',
  partiallyRefunded: 'standard'
};
function getPaymentStatusTooltip(displayedPaymentMethodStatus) {
  if (!displayedPaymentMethodStatus) {
    return '';
  } else {
    switch (displayedPaymentMethodStatus.type) {
      case InvoicePaymentDisplayStatusType.Pending:
        return 'Automatic payment processing will begin shortly';
      case InvoicePaymentDisplayStatusType.Processing:
        return 'Processing the payment may take up to 3 business days';
      case InvoicePaymentDisplayStatusType.Collected:
        return "The payment will be deposited to your bank account within 1-2 business days";
      case InvoicePaymentDisplayStatusType.RefundRequested:
        return 'Upon completion of payment processing, a refund will be issued';
      default:
        return '';
    }
  }
}
function getPaymentStatusSkin(displayedPaymentMethodStatus) {
  if (!displayedPaymentMethodStatus) {
    return undefined;
  } else {
    switch (displayedPaymentMethodStatus.type) {
      case InvoicePaymentDisplayStatusType.PaymentMethodNotSet:
      case InvoicePaymentDisplayStatusType.PaymentFailed:
      case InvoicePaymentDisplayStatusType.StoppedPayment:
        return 'error';
      case InvoicePaymentDisplayStatusType.PaymentMethodUnverified:
      case InvoicePaymentDisplayStatusType.RequiresPaymentApproval:
      case InvoicePaymentDisplayStatusType.RequiresPaymentValidation:
        return 'warning';
      default:
        return undefined;
    }
  }
}
function InvoicesTableRow(props) {
  const {
    invoice,
    hideDueDate,
    showQboIntegration,
    showXeroIntegration,
    to,
    retry,
    onUnvoidInvoice,
    onUnmarkPaidExternally
  } = props;
  const overlayContext = useOverlayContext();
  const {
    markAsPaid,
    shouldShowMarkAsPaid
  } = usePaidExternally();
  const {
    resendInvoice
  } = useResendInvoice();
  const history = useHistory();
  const isVendor = useStore(selectors.user.isVendor);
  const email = useStore(selectors.user.email);
  const testId = "invoice-row";
  const {
    api,
    logBI
  } = useStoreActions();
  const {
    copyDirectLink
  } = useCopyDirectLink();
  const {
    downloadInvoicePdf
  } = useDownloadInvoicePdf();
  const paymentStatusTooltip = getPaymentStatusTooltip(invoice.paymentDisplayStatus);
  const {
    XERO_INTEGRATION,
    UNVOID_INVOICE = false,
    UNMARK_PAID_EXTERNALLY = false
  } = useFeatureFlags();
  const paymentStatusText = useMemo(() => {
    return getPaymentStatusText(invoice, isVendor);
  }, [invoice, isVendor]);
  const onVoidInvoice = () => {
    overlayContext.showModal({
      closeOnClickOutside: true,
      closeOnEscapeKey: true,
      content: _ref => {
        let {
          dismissModal
        } = _ref;
        return /*#__PURE__*/_jsx(ConfirmationModal, {
          title: "Void Invoice",
          content: "Are you sure you want to void invoice ".concat(invoice.formattedNumber, "?"),
          "data-testid": "void-confirmation-modal",
          primaryButtonLabel: "I'm sure",
          secondaryButtonLabel: "Cancel",
          onSecondaryButtonClick: () => dismissModal(),
          onPrimaryButtonClick: async () => {
            try {
              overlayContext.startLoader(voidLoader);
              await api.invoice.void(invoice.id);
              await retry();
              dismissModal(true);
            } catch (error) {
              overlayContext.showErrorToast('Unable to void the invoice, please try again', 'void-invoice-error');
              dismissModal();
            } finally {
              overlayContext.stopLoader(voidLoader);
            }
          }
        });
      }
    });
  };
  return /*#__PURE__*/_jsx(TableListRow, {
    as: Link,
    to: to,
    size: "large",
    "data-testid": testId,
    "data-heapid": "invoice",
    items: compact([{
      className: styles.invoiceIdCell,
      value: /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(Typography, {
          "data-testid": "".concat(testId, "-invoice-no"),
          skin: "secondary",
          className: styles.invoiceId,
          children: invoice.formattedNumber
        }), showQboIntegration && /*#__PURE__*/_jsx(InvoiceAccountingIntegrationStatus, {
          testid: "".concat(testId, "-qbo"),
          invoiceIntegration: invoice.integration,
          accountingType: Accounting.QBO
        }), showXeroIntegration && XERO_INTEGRATION && /*#__PURE__*/_jsx(InvoiceAccountingIntegrationStatus, {
          testid: "".concat(testId, "-xero"),
          invoiceIntegration: invoice.xeroIntegration.getIntegration(),
          accountingType: Accounting.Xero
        })]
      })
    }, {
      value: /*#__PURE__*/_jsxs(Typography, {
        ellipsis: true,
        weight: "normal",
        "data-testid": "".concat(testId, "-comp-name"),
        children: [invoiceModel.otherPartyCompanyName(invoice, isVendor), "\xA0"]
      })
    }, {
      value: /*#__PURE__*/_jsx(Typography, {
        skin: displayedStatusToSkin[invoice.displayStatus],
        weight: "normal",
        "data-testid": "".concat(testId, "-status"),
        children: getStatusLabel(invoice.displayStatus)
      })
    }, {
      value: paymentStatusText && /*#__PURE__*/_jsx(WithInfoIcon, {
        info: paymentStatusTooltip,
        showInfo: !!paymentStatusTooltip,
        children: /*#__PURE__*/_jsx(Typography, {
          skin: getPaymentStatusSkin(invoice.paymentDisplayStatus),
          "data-testid": "".concat(testId, "-payment-status"),
          children: paymentStatusText
        })
      })
    }, {
      value: /*#__PURE__*/_jsx(Typography, {
        skin: "secondary",
        "data-testid": "".concat(testId, "-issued-on"),
        children: invoiceModel.issueDate(invoice)
      })
    }, !hideDueDate && {
      value: /*#__PURE__*/_jsx(Typography, {
        skin: "secondary",
        "data-testid": "".concat(testId, "-due-date"),
        children: invoice.dueDate
      })
    }, {
      value: /*#__PURE__*/_jsx(Typography, {
        weight: "light",
        skin: "secondary",
        "data-testid": "".concat(testId, "-amount"),
        ellipsis: true,
        children: invoice.totalAmount
      })
    }]),
    actionColumn: /*#__PURE__*/_jsxs(TableListActions, {
      buttonText: "View",
      onClickButton: () => history.push(to),
      testId: testId,
      btnHeapId: "invoice-view-btn",
      menuHeapId: "invoice-menu-btn",
      children: [shouldShowMarkAsPaid(invoiceModel.statusType(invoice), isVendor) && /*#__PURE__*/_jsx(DropdownMenuItem, {
        intent: Intent.InvoiceMarkPaidExternally,
        "data-testid": "".concat(testId, "-paid-externally"),
        "data-heapid": "invoice-menu-paid-externally",
        iconStart: /*#__PURE__*/_jsx(MoneyReceive, {}),
        onClick: () => markAsPaid(invoice, retry),
        children: "Mark as paid externally"
      }), allowResendAction(invoiceModel.statusType(invoice), isVendor) && /*#__PURE__*/_jsx(DropdownMenuItem, {
        intent: Intent.InvoiceResend,
        "data-testid": "".concat(testId, "-resend-invoice"),
        "data-heapid": "invoice-menu-resend-invoice",
        iconStart: /*#__PURE__*/_jsx(MailSentLine18, {
          width: 24,
          height: 24
        }),
        onClick: () => {
          resendInvoice(invoice).then(retry);
        },
        children: "Resend invoice"
      }), /*#__PURE__*/_jsx(DropdownMenuItem, {
        intent: Intent.InvoiceDownload,
        "data-testid": "".concat(testId, "-download-invoice"),
        "data-heapid": "invoice-menu-download-invoice",
        iconStart: /*#__PURE__*/_jsx(Download, {}),
        onClick: () => downloadInvoicePdf(invoice.id),
        disabled: !invoice.pdfExists,
        tooltipProps: invoice.pdfExists ? undefined : {
          title: 'Generating PDF'
        },
        children: "Download invoice"
      }), /*#__PURE__*/_jsx(DropdownMenuItem, {
        "data-testid": "".concat(testId, "-agreement"),
        "data-heapid": "invoice-menu-agreement",
        as: Link,
        to: routes.agreement(invoiceModel.relationshipId(invoice)),
        iconStart: /*#__PURE__*/_jsx(AgreementsListLine, {}),
        children: "View agreement"
      }), allowVoidAction(invoiceModel.statusType(invoice), isVendor) && /*#__PURE__*/_jsx(DropdownMenuItem, {
        intent: Intent.InvoiceVoid,
        iconStart: /*#__PURE__*/_jsx(Void, {}),
        onClick: onVoidInvoice,
        "data-testid": "".concat(testId, "-void"),
        "data-heapid": "invoice-menu-void",
        children: "Void invoice"
      }), allowUnvoidAction(invoice, isVendor, UNVOID_INVOICE) && /*#__PURE__*/_jsx(DropdownMenuItem, {
        intent: Intent.InvoiceUnvoid,
        iconStart: /*#__PURE__*/_jsx(RedoLiner24, {}),
        onClick: onUnvoidInvoice,
        "data-testid": "".concat(testId, "-unvoid"),
        "data-heapid": "invoice-menu-unvoid",
        children: "Unvoid invoice"
      }), allowUnmarkPaidExternallyAction(invoice, isVendor, UNMARK_PAID_EXTERNALLY) && /*#__PURE__*/_jsx(DropdownMenuItem, {
        intent: Intent.InvoiceUnmarkPaidExternally,
        iconStart: /*#__PURE__*/_jsx(RedoLiner24, {}),
        onClick: onUnmarkPaidExternally,
        "data-testid": "".concat(testId, "-unmark-paid-externally"),
        "data-heapid": "invoice-menu-unmark-paid-externally",
        children: "Unmark as paid externally"
      }), /*#__PURE__*/_jsx(DropdownMenuItem, {
        intent: Intent.InvoiceCopyLink,
        "data-testid": "".concat(testId, "-copy-link"),
        "data-heapid": "invoice-menu-copy-link",
        iconStart: /*#__PURE__*/_jsx(LinkIcon, {}),
        onClick: () => {
          if (isVendor) {
            logBI(14001, {
              email
            });
          } else {
            logBI(14003);
          }
          copyDirectLink(invoiceModel.relationshipId(invoice), {
            type: 'invoice',
            id: invoice.id
          });
        },
        children: "Copy link"
      })]
    })
  }, invoice.id);
}
export default /*#__PURE__*/memo(InvoicesTableRow);